import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCalendlyDemo } from "@/lib/analytics";
import {
  ArrowRight,
  CheckCircle2,
  Network,
  Database,
  Layers,
  ShieldCheck,
  Zap,
  Calendar,
  GitMerge,
  Globe,
  FileText,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ROIResultsCapture from '@/components/ROIResultsCapture';
import Certifications from '@/components/Certifications';

const CALENDLY = 'https://outlook.office.com/bookwithme/user/ca2e84f5d95b4280a34073c39ee722f3@ezcloud.ai/meetingtype/cGjhRG1JmkSrBrj2cFWWjA2?anonymous&ismsaljsauthenabled&ep=mlink';

const EZCLOUD_TO_ORACLE = [
  { label: 'AP invoice headers + lines', note: 'Including project, task, and cost-centre distribution' },
  { label: '3-way match results', note: 'Matched invoices post straight through with PO + receipt references' },
  { label: 'Approval metadata', note: 'Approver, timestamp, and attached evidence for audit' },
  { label: 'Linked invoice images', note: 'Attached to the Oracle invoice record, always available' },
  { label: 'Tax + withholding codes', note: 'Applied per supplier + jurisdiction rules' },
];

const ORACLE_TO_EZCLOUD = [
  'Supplier master (active vendors + banking)',
  'Chart of accounts + cost centres',
  'Open purchase orders + receipts',
  'Operating unit / business unit hierarchy',
  'Payment terms + tax codes',
  'Project / task / award structures (EBS Projects, Fusion PPM)',
];

const DAY_ONE = [
  {
    icon: GitMerge,
    title: '3-way match automation',
    body: "Match invoice lines to PO and receipt, tolerance-handle quantity and price variances, and auto-post exceptions into an approval queue — or block them if they breach your thresholds.",
  },
  {
    icon: Globe,
    title: 'Multi-BU and multi-currency',
    body: "Route invoices into the correct operating unit / business unit, handle intercompany routing, and translate to your ledger currency using Oracle's exchange-rate source of truth.",
  },
  {
    icon: Layers,
    title: 'Project, task, award coding',
    body: 'For grant- or project-heavy orgs (higher-ed, federal contractors, R&D-heavy manufacturing), EZ Cloud reads your Oracle project hierarchy and routes invoices to project managers for coding and approval.',
  },
  {
    icon: ShieldCheck,
    title: 'SOX-ready audit trail',
    body: 'Every action — capture, coding change, approver, timestamp, attached email thread — is recorded and exportable. SOC 2 compliant environment.',
  },
];

const FAQS = [
  {
    q: 'Does EZ Cloud integrate with Oracle EBS, Oracle Fusion Cloud, or both?',
    a: 'Both. We support Oracle E-Business Suite (R12) and Oracle Fusion Cloud ERP (OCI). The integration uses Oracle-standard REST APIs on Fusion and a combination of REST, SOAP, and direct table-safe writes on EBS. Same EZ Cloud experience either way.',
  },
  {
    q: 'Do you support Oracle PeopleSoft and JD Edwards, or only EBS and Fusion?',
    a: 'All four Oracle ERPs are core to what we do — Oracle E-Business Suite, Fusion Cloud, PeopleSoft, and JD Edwards — and squarely in our wheelhouse. EBS and Fusion are simply the most common in the install base; PeopleSoft and JD Edwards are an equal, first-class fit, not secondary. For PeopleSoft we post AP vouchers through the supported voucher load (Voucher Build); for JD Edwards we load the standard AP batch interface (F0411Z1) and run JDE’s Batch Voucher Processor. Every Oracle ERP posts through its own Oracle-supported method — no direct base-table writes — with the same upstream AP layer (email exception prevention, capture, coding, approval) in front. Running PeopleSoft or JDE? Talk to us about your AP flow.',
  },
  {
    q: "Oracle already has an AP module. Why layer EZ Cloud on top?",
    a: "Oracle's AP module is a general ledger posting endpoint — excellent at what it does. EZ Cloud handles everything that happens before a clean invoice reaches Oracle: capture (AI data extraction, email, portal), classification, 3-way match logic, exception handling, approval routing, and the AI email prevention layer that resolves supplier issues upstream. We post finished invoices into Oracle; we don't replace it.",
  },
  {
    q: 'How does EZ Cloud handle Oracle multi-BU / multi-OU environments?',
    a: 'Out of the box. EZ Cloud reads your full operating-unit / business-unit hierarchy from Oracle, routes invoices to the correct BU during capture, and supports intercompany invoice flows without duplicate keying. Approvers see only the BUs they belong to.',
  },
  {
    q: 'What does implementation look like?',
    a: 'Typical timeline is 8–12 weeks (Fusion Cloud often lands toward the shorter end). EBS posts through the standard AP Invoice Open Interface; Fusion Cloud loads via FBDI with real-time validation over Oracle Integration Cloud (OIC) REST. The critical-path items are supplier-master cleanup, approval-matrix mapping, and sandbox testing with a representative invoice sample. We do the integration work; you provide the Oracle access and sign-off.',
  },
  {
    q: 'Is EZ Cloud on the Oracle Cloud Marketplace?',
    a: 'Contact sales for current marketplace status. The integration itself uses standard Oracle APIs and does not require marketplace listing to deploy.',
  },
  {
    q: 'Does EZ Cloud work with OTBI / Oracle Analytics for reporting?',
    a: "Yes. All AP activity EZ Cloud posts to Oracle is fully visible in OTBI / Oracle Analytics using the standard AP subject areas. EZ Cloud also provides its own operational dashboards (invoice aging, exception root-cause, approver bottlenecks) that pull from both sides.",
  },
  {
    q: 'Is it SOC 2 certified? HIPAA-compliant?',
    a: 'Yes — SOC 2 Type II certified. HIPAA-compliant workflows are available for healthcare Oracle customers; contact the security team about BAA specifics.',
  },
];

const Oracle = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [roiInputs, setRoiInputs] = useState({ invoices: 5000, costPerInvoice: 18, staffRate: 38 });
  const annualSavings = useMemo(() => {
    const minutesSavedPerInvoice = 12;
    const hoursSaved = (roiInputs.invoices * minutesSavedPerInvoice) / 60;
    const laborMonthly = hoursSaved * roiInputs.staffRate;
    const processingMonthly = roiInputs.invoices * roiInputs.costPerInvoice * 0.55;
    return Math.max(0, (laborMonthly + processingMonthly) * 12);
  }, [roiInputs]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle EBS & Fusion Cloud Invoice & AP Automation on OCI | EZ Cloud"
        description="Invoice automation and AP automation for the Oracle ERP family — E-Business Suite, Fusion Cloud, PeopleSoft, and JD Edwards. Published on the Oracle Reference Architecture for OCI. AI invoice data extraction, 3-way match, multi-BU routing, project coding, SOX-ready audit, deployed in your OCI tenancy. See a live demo."
        canonical="https://ezcloud.ai/integrations/oracle"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Integrations', url: 'https://ezcloud.ai/integrations' },
          { name: 'Oracle', url: 'https://ezcloud.ai/integrations/oracle' },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <Breadcrumbs />

      {/* Hero */}
      <section className="bg-hero-gradient text-white pt-16 pb-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent"></div>
          <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 left-20 w-64 h-64 bg-white/5 rounded-full blur-2xl"></div>
        </div>

        <div className="relativecontainer mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
                <Network className="w-4 h-4" aria-hidden />
                Integration · Oracle EBS + Fusion Cloud
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                AP automation built for the way Oracle enterprises actually run.
              </h1>
              <p className="text-xl md:text-2xl text-white/80 mb-6">
                3-way match, multi-BU routing, project coding, SOX-ready audit trail. Prebuilt integrations with Oracle E-Business Suite and Oracle Fusion Cloud, built and maintained by EZ Cloud — no data duplication.
              </p>
              <div className="flex flex-wrap items-center gap-3 mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm text-white/90">
                  <ShieldCheck className="w-4 h-4 text-accent" aria-hidden />
                  <span>
                    Published on the{' '}
                    <a
                      href="https://docs.oracle.com/en/solutions/ez-cloud-on-oci/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
                    >
                      Oracle Reference Architecture for OCI
                    </a>
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm text-white/90">
                  <FileText className="w-4 h-4 text-accent" aria-hidden />
                  <span>
                    Featured in{' '}
                    <a
                      href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold underline decoration-accent/50 underline-offset-2 hover:decoration-accent"
                    >
                      Oracle's technical case studies
                    </a>
                  </span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  variant="accent"
                  className="font-semibold"
                  onClick={() => openCalendlyDemo()}
                >
                  <Calendar className="mr-2 w-5 h-5" aria-hidden />
                  See Oracle integration live
                  <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
                </Button>
                <Button size="lg" variant="outline-on-dark" asChild>
                  <Link to="/technology/native-erp">How our ERP integration works</Link>
                </Button>
              </div>
            </div>

            {/* Data-flow visualization */}
            <Card className="p-6 bg-white/10 border-white/20 backdrop-blur-xl">
              <h2 className="text-white font-semibold mb-4">How the integration works</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-accent/20 rounded-lg border border-accent/30">
                  <div className="font-bold text-white">EZ Cloud</div>
                  <ArrowRight className="w-4 h-4 text-accent mx-auto" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Posts approved invoices, 3-way match results, approval trail, images</div>
                </div>
                <div className="text-center text-xs text-white/50 py-1">Oracle standard APIs (REST / SOAP) · bi-directional</div>
                <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                  <div className="font-bold text-white">Oracle</div>
                  <ArrowRight className="w-4 h-4 text-gray-600 mx-auto rotate-180" aria-hidden />
                  <div className="text-sm text-white/80 flex-1">Returns supplier master, COA, open POs + receipts, BU hierarchy, tax</div>
                </div>
              </div>
              <p className="text-xs text-white/50 mt-4">
                Works with EBS R12 and Fusion Cloud ERP. No Oracle customisation required.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Oracle WebCenter cross-link — high-value internal link to the Modernize page */}
      <section className="bg-accent/10 border-b border-accent/20">
        <div className="container mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-center text-gray-800">
          <span className="font-semibold">
            Running Oracle WebCenter AP automation today?
          </span>
          <Link
            to="/solutions/modernize-oracle-webcenter"
            className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
          >
            See the Oracle WebCenter AP modernization path — 11g, 12c, 14c
            <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
          </Link>
        </div>
      </section>

      {/* Oracle AP heritage */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
            25 years of Oracle AP, not a generic connector
          </p>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
            EZ Cloud was founded by{' '}
            <a
              href="/about"
              className="font-semibold text-gray-900 underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
            >
              Andrew Blackman
            </a>
            , an Oracle accounts-payable specialist with 25 years inside Oracle invoice
            processing. We don't adapt a multi-ERP product to Oracle — Oracle is where we
            started. That shows up in the details: BU hierarchy handling, project &amp; award
            coding, tax and withholding logic, and a 3-way match that respects how your Oracle
            tolerances are actually configured.
          </p>
          <p className="mt-6 text-sm text-gray-500 max-w-3xl mx-auto">
            Per Oracle's own technical case study, EZ Cloud helps companies capture, authorize, and pay
            invoices{' '}
            <a
              href="https://www.oracle.com/cloud/technical-case-studies/ez-cloud/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
            >
              up to 80% faster than on-premises AP applications
            </a>
            , running natively on Oracle Cloud Infrastructure.
          </p>
        </div>
      </section>

      {/* Founder credentials */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <Certifications
            heading="Led by Oracle-certified expertise"
            subheading="EZ Cloud is founder-led by an Oracle-certified specialist. Every credential below verifies directly on Oracle."
          />
        </div>
      </section>

      {/* Industry solutions on Oracle */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            Industry solutions on Oracle
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-3xl mx-auto">
            Deep, vertical-specific AP automation for the industries that run on Oracle.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <Link
              to="/solutions/healthcare-oracle-fusion"
              className="group block p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-accent/50 hover:shadow-md transition-all"
            >
              <div className="text-accent text-xs font-semibold uppercase tracking-wide mb-2">
                Healthcare · Oracle Fusion
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center justify-between">
                Healthcare on Oracle Fusion
                <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-accent group-hover:translate-x-0.5 transition-all flex-shrink-0 ml-2" aria-hidden />
              </h3>
              <p className="text-sm text-gray-600">
                HIPAA-compliant AP automation, OCI-native deployment, and AI email exception prevention for health systems on Oracle Fusion Cloud ERP.
              </p>
            </Link>
            <Link
              to="/solutions/education-oracle-ebs"
              className="group block p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:border-accent/50 hover:shadow-md transition-all"
            >
              <div className="text-accent text-xs font-semibold uppercase tracking-wide mb-2">
                K-12 · Oracle EBS
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center justify-between">
                Education on Oracle EBS
                <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-accent group-hover:translate-x-0.5 transition-all flex-shrink-0 ml-2" aria-hidden />
              </h3>
              <p className="text-sm text-gray-600">
                Fund accounting, encumbrances, grants, and district tax for K-12 and higher education on Oracle E-Business Suite.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Data flow detail */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            What flows between EZ Cloud and Oracle
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Real-time, bi-directional. No staging database, no CSV exports, no nightly batch.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                  <ArrowRight className="w-5 h-5 text-accent" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">EZ Cloud → Oracle</h3>
              </div>
              <ul className="space-y-3">
                {EZCLOUD_TO_ORACLE.map(({ label, note }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" aria-hidden />
                    <div>
                      <div className="text-gray-900 font-medium">{label}</div>
                      <div className="text-sm text-gray-500">{note}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Database className="w-5 h-5 text-primary" aria-hidden />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Oracle → EZ Cloud</h3>
              </div>
              <ul className="space-y-3">
                {ORACLE_TO_EZCLOUD.map((label) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden />
                    <div className="text-gray-900 font-medium">{label}</div>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 mt-4">
                EZ Cloud reads Oracle's supplier master and hierarchy at capture time so every invoice is coded against current data — no stale reference tables.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* OCI deployment + security */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
                <ShieldCheck className="w-4 h-4" aria-hidden />
                Built for OCI
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Deploy inside your own Oracle Cloud Infrastructure
              </h2>
              <p className="text-gray-600 mb-6">
                For Oracle Fusion and EBS customers running on OCI, EZ Cloud deploys
                into a peered tenancy connected to your environment over the Oracle
                backbone — not the public internet. The architecture is the one
                published on Oracle's own reference architecture pages.
              </p>
              <ul className="space-y-3">
                {[
                  'VCN peering across the Oracle backbone — no traffic over the public internet',
                  'No public IP addresses exposed on the integration layer',
                  'Customer-controlled firewall and security lists',
                  'Data residency in your chosen OCI region (UK, AU, EU, US, and US-gov regions)',
                  'AES-256 encryption in transit and at rest; SOC 2 Type II certified',
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="p-6 bg-gray-50 border border-gray-200">
              <div className="space-y-3 text-sm">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="font-semibold text-gray-900">Customer OCI Tenancy</div>
                  <div className="text-gray-500">Oracle Fusion / EBS · your VCN</div>
                </div>
                <div className="text-center text-xs text-gray-500">
                  ↕ VCN peering · Oracle backbone · no public internet
                </div>
                <div className="rounded-lg border border-accent/30 bg-accent/10 p-4">
                  <div className="font-semibold text-gray-900">EZ Cloud OCI Tenancy</div>
                  <div className="text-gray-500">Capture · coding · workflow · AI prevention</div>
                </div>
                <p className="text-xs text-gray-500 pt-2">
                  No internet exposure · no public IPs · customer-controlled firewall.
                  Mirrors the EZ Cloud OCI reference architecture published by Oracle.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Day-one capabilities */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
            What you get from day one
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            The four capabilities Oracle AP teams ask about most — all live on day one, not in a future phase.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {DAY_ONE.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="p-5 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  <Icon className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-gray-600">{body}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Oracle-supported integration methods (implementation trust) */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Lower-risk by design
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built on Oracle-supported integration methods
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The fastest way to lose an Oracle finance team's trust is to touch the
              database directly. EZ Cloud doesn't. We post through the same interfaces
              Oracle publishes for exactly this purpose — so your DBAs, auditors, and
              Oracle support all stay comfortable.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-gray-50 border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Oracle EBS</h3>
              <ul className="space-y-3">
                {[
                  'Posts via the standard AP Invoice Open Interface — never direct writes to AP base tables',
                  'Oracle’s own import program carries invoices through with full validation',
                  'Concurrent-program and Open Interface patterns your DBAs already know',
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-6 bg-gray-50 border border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Oracle Cloud ERP (Fusion)</h3>
              <ul className="space-y-3">
                {[
                  'Invoice load via FBDI — Oracle’s File-Based Data Import framework',
                  'Real-time validation through Oracle Integration Cloud (OIC) REST',
                  'No customization of the Oracle data model; governance and audit preserved',
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <p className="text-center text-sm text-gray-500 mt-6 max-w-3xl mx-auto">
            No direct database modification. No unsupported customizations. Every posting
            uses an Oracle-published interface, so audit, security, and Oracle support
            posture are unchanged.
          </p>
        </div>
      </section>

      {/* Full Oracle ERP family — PeopleSoft + JD Edwards */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              The whole Oracle ERP family
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Also running PeopleSoft or JD Edwards?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              EZ Cloud automates AP across the entire Oracle ERP family — all four are squarely in our
              wheelhouse. Most Oracle shops run EBS or Fusion, so those come up most often, but PeopleSoft
              and JD Edwards are an equal, first-class fit — not an afterthought. The same operational layer —
              supplier-email exception prevention, capture, coding, and approval routing — sits in front of
              every one, posting through each platform’s own Oracle-supported voucher interface. No direct
              table writes.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Oracle PeopleSoft Financials</h3>
              <ul className="space-y-3">
                {[
                  'Posts AP vouchers through PeopleSoft’s supported voucher load (Voucher Build via the voucher staging interface) — never direct writes to PeopleSoft tables',
                  'ChartField coding, multi-business-unit routing, and approval handled upstream before the voucher lands',
                  'Supplier-email exception prevention and AI capture sit in front, exactly as they do for EBS and Fusion',
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-6 bg-white border border-gray-200 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Oracle JD Edwards EnterpriseOne</h3>
              <ul className="space-y-3">
                {[
                  'Loads vouchers into JDE’s standard AP batch interface (F0411Z1) and runs the Batch Voucher Processor — JDE’s supported voucher import, no custom table writes',
                  'Business-unit / object / subsidiary coding resolved before posting',
                  'Same upstream AP operations layer: email prevention, capture, coding, and routing',
                ].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>
          <p className="text-center text-gray-600 mt-8">
            On PeopleSoft or JD Edwards?{' '}
            <button
              onClick={() => openCalendlyDemo()}
              className="font-semibold text-primary hover:text-accent transition-colors underline underline-offset-2"
            >
              Talk to us about your AP flow
            </button>{' '}
            — we’ll map it to your environment.
          </p>
        </div>
      </section>

      {/* Industry fit */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Typical EZ Cloud + Oracle customer
          </h2>
          <p className="text-gray-600 mb-8 max-w-3xl">
            Oracle is the dominant ERP in mid-to-enterprise finance. EZ Cloud's Oracle integration is in production at organisations matching this profile:
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              'Enterprise or large mid-market on Oracle EBS, Fusion Cloud, PeopleSoft, or JD Edwards',
              'Multi-entity, multi-business-unit, often multi-country',
              '5,000 – 100,000+ invoices/month across the group',
              'High 3-way-match volume (manufacturing, healthcare, distribution)',
              'Project, grant, or capex-heavy coding',
              'SOX / audit scrutiny on AP controls',
            ].map((x) => (
              <div key={x} className="flex items-start gap-2 text-gray-600">
                <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                <span>{x}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/industries/healthcare"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors"
            >
              Healthcare <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
            <Link
              to="/industries/manufacturing"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors"
            >
              Manufacturing <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
            <Link
              to="/industries/education"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-900 hover:bg-gray-100 transition-colors"
            >
              Education <ArrowRight className="w-3 h-3" aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Rough Oracle ROI
              </h2>
              <p className="text-gray-600 mb-6">
                Defaults reflect a typical mid-enterprise Oracle AP team. For a closer estimate, use your industry's calculator or the full ROI tool.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" asChild>
                  <Link to="/resources/roi-calculator">Full ROI calculator</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/industries/healthcare">Healthcare calculator</Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/industries/manufacturing">Manufacturing calculator</Link>
                </Button>
              </div>
            </div>

            <Card className="p-6 bg-accent/10 border-accent/30">
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Monthly invoices</label>
                  <input
                    type="number"
                    min={0}
                    value={roiInputs.invoices}
                    onChange={(e) =>
                      setRoiInputs({ ...roiInputs, invoices: Math.max(0, Number(e.target.value) || 0) })
                    }
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">Cost per invoice ($)</label>
                  <input
                    type="number"
                    min={0}
                    value={roiInputs.costPerInvoice}
                    onChange={(e) =>
                      setRoiInputs({ ...roiInputs, costPerInvoice: Math.max(0, Number(e.target.value) || 0) })
                    }
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">AP staff rate ($/hr)</label>
                  <input
                    type="number"
                    min={0}
                    value={roiInputs.staffRate}
                    onChange={(e) =>
                      setRoiInputs({ ...roiInputs, staffRate: Math.max(0, Number(e.target.value) || 0) })
                    }
                    className="w-full rounded-md bg-white border border-gray-300 px-3 py-2 text-gray-900"
                  />
                </div>
              </div>
              <div className="mb-6">
                <div className="text-sm text-gray-700 mb-1">Estimated annual savings</div>
                <div className="text-4xl font-bold text-accent">
                  ${Math.round(annualSavings).toLocaleString()}
                </div>
              </div>
              <ROIResultsCapture
                industry="Oracle Integration"
                annualSavings={annualSavings}
                inputs={{
                  'Monthly invoices': roiInputs.invoices.toLocaleString(),
                  'Cost per invoice': `$${roiInputs.costPerInvoice}`,
                  'Staff hourly rate': `$${roiInputs.staffRate}`,
                }}
                buttonLabel="Email my Oracle ROI report"
              />
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
              Oracle integration FAQ
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="border border-gray-200 rounded-lg px-6 py-2 shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline hover:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pt-2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to see EZ Cloud running in your Oracle?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            30-minute demo. Bring your invoice sample — we'll walk a live 3-way match against your PO data.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              variant="accent"
              onClick={() => openCalendlyDemo()}
            >
              Book an Oracle demo
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/technology/native-erp">
                <FileText className="mr-2 w-5 h-5" aria-hidden />
                How native integration works
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Oracle;
